import type { ResultType } from '../constants/global';

/**
 * Types for the result-ingestion saga.
 *
 * Posting a result to a V2 create endpoint starts a MassTransit state machine that runs the
 * result through SQL projection, an agent callback, AI document extraction and — at polling
 * unit only — an iREV fetch. This module models what that machine reports back.
 *
 * Distinct from the three other "conflict" axes already in this codebase. Do NOT overload:
 *   - `IResultAnalyticsData.hasConflict`  — opaque upload badge
 *   - `assignmentMeta.hasConflict`        — disputed assignment
 *   - the in-result `mismatch`            — sum of party votes != recorded valid votes
 *   - reconciliation severity             — an entity's own result vs the sum of its children
 * The saga `mismatch` below is a *fifth* axis: it is the pipeline's own validation verdict on
 * one submission, produced by comparing extraction output against the submitted figures.
 */

/**
 * States the four state machines declare. `AwaitingIrev` is reachable at polling unit and is
 * commonly observed — it is absent from the backend handoff document, so anything that
 * enumerates states from that document will silently drop those rows.
 */
export type SagaCurrentState =
	| 'Initial'
	| 'Processing'
	| 'AwaitingIrev'
	| 'Completed'
	| 'CompletedWithWarnings'
	| 'PermanentlyFailed'
	| 'Final';

/** Serialized as the enum NAME, not an integer. */
export type SagaMismatch = 'None' | 'AiValidationFailed' | 'IrevValidationFailed';

/** Which paged saga endpoint a result level reads from. */
export type SagaLevel = 'pu' | 'ward' | 'lga' | 'state';

export const SAGA_LEVEL_BY_RESULT_TYPE: Partial<Record<ResultType, SagaLevel>> = {
	EC8A: 'pu',
	EC8B: 'ward',
	EC8C: 'lga',
	EC8D: 'state',
};

/**
 * The filter key naming the entity code differs per level — confirmed against the deployed
 * OpenAPI spec. Sending the wrong one is not an error: the server ignores unknown JSON
 * properties, so the filter is dropped and the response comes back unfiltered.
 */
export const SAGA_CODE_FILTER_KEY: Record<SagaLevel, string> = {
	pu: 'pollingUnitCodes',
	ward: 'wardCodes',
	lga: 'lgaCodes',
	state: 'stateCodes',
};

/** Flags every level reports. */
export interface ISagaRowBase {
	correlationId: string;
	currentState: SagaCurrentState;
	resultId: string;
	isSqlSyncCompleted: boolean;
	isAgentCallbackCompleted: boolean;
	isAiExtractionCompleted: boolean;
	isAiExtractionFailed: boolean;
	isAiCallbackCompleted: boolean;
	mismatch: SagaMismatch;
	electionId: string;
	requestingUserId: string;
	createdAt: string;
	updatedAt: string;
	version: number;
}

/** Polling unit additionally reports the iREV branch. */
export interface IPuSagaRow extends ISagaRowBase {
	pollingUnitCode: string;
	isIrevExtractionCompleted: boolean;
	isIrevCallbackCompleted: boolean;
	/** Server-side poll attempts against iREV. Capped at 60. Never render this to an agent. */
	irevPollCount: number;
	irevPollTokenId: string | null;
}

export interface IWardSagaRow extends ISagaRowBase {
	wardCode: string;
	localGovernmentCode: string;
	stateCode: string;
}

export interface ILgaSagaRow extends ISagaRowBase {
	lgaCode: string;
	stateCode: string;
}

export interface IStateSagaRow extends ISagaRowBase {
	stateCode: string;
}

export type ISagaRow = IPuSagaRow | IWardSagaRow | ILgaSagaRow | IStateSagaRow;

/**
 * The saga list envelope. **Bare — not wrapped in the house `BaseResponse`**, unlike the
 * dead-result endpoints on the same service. Verified live: the keys are `total` /
 * `currentPage` / `pageSize` / `lastPage` / `data`, NOT `items` / `totalCount` / `pageNumber`.
 */
export interface ISagaPagedResponse<T = ISagaRow> {
	total: number;
	currentPage: number;
	pageSize: number;
	lastPage: number;
	data: T[];
}

/** Server caps this and silently clamps rather than erroring. */
export const SAGA_MAX_PAGE_SIZE = 50;

/** What an agent is told, derived from the row. Ordered by precedence, most urgent first. */
export type SagaVerdict = 'stalled' | 'reviewFigures' | 'checkPhoto' | 'landed' | 'working' | 'unknown';

/**
 * `isAiExtractionCompleted` and `isAiExtractionFailed` are independent booleans, so all four
 * combinations occur. `warn` is the both-true case: a failure was recorded and the extraction
 * later succeeded. Data exists, so it must not be presented as a failure.
 */
export type AiState = 'pending' | 'done' | 'failed' | 'warn';

export type StepKey = 'saved' | 'confirmed' | 'ai' | 'irev';

export type StepState = 'pending' | 'active' | 'done' | 'failed' | 'warn';

export interface ISagaStep {
	key: StepKey;
	/** Agent-facing label. Never an enum name. */
	label: string;
	state: StepState;
	/** One line of plain-language context, or '' when there is nothing useful to add. */
	note: string;
}

export interface ISagaView {
	verdict: SagaVerdict;
	/** Headline. A verb when the agent must act, a statement otherwise. */
	headline: string;
	/** One or two sentences under the headline. */
	detail: string;
	steps: ISagaStep[];
	/** Present only when the agent can actually do something about it. */
	action?: { label: string; kind: 'reupload' | 'compare' | 'report' | 'retryAi' };
	/** True once the agent's own obligation is discharged, regardless of later stages. */
	isLanded: boolean;
}
